/**
 * Parse single-plot chat commands for axis zoom / "cut" without calling the LLM.
 * Supported (case-insensitive), examples:
 * - reset zoom | reset axis | autoscale | full range | show full plot
 * - x 0-100 | x: 0 to 100 | x axis 1, 9 | zoom x 0 100
 * - y 10-50 | zoom y 1 9
 * - z 0-1 (3D scatter)
 * - x 0-100 y 5-20 (combined on one line)
 */

const NUM = "([\\-+]?\\d*\\.?\\d+(?:[eE][\\-+]?\\d+)?)";

function normalizePair(a, b) {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  return x <= y ? [x, y] : [y, x];
}

function axisRangeRegex(letter) {
  return new RegExp(
    `\\b${letter}\\s*(?:axis)?\\s*[:=]?\\s*${NUM}\\s*(?:-|–|to|,|\\s+)\\s*${NUM}\\b`,
    "i"
  );
}

export function parsePlotViewZoomChatMessage(raw) {
  if (typeof raw !== "string") {
    return null;
  }
  let t = raw.trim();
  if (!t) {
    return null;
  }
  t = t.replace(/^(please|can you|kindly)[,:.\s]+/i, "").trim();
  const compact = t.replace(/\s+/g, " ");

  if (
    /^(reset zoom|reset axis|reset view|autoscale|auto scale|full range|show full plot)\s*\.?$/i.test(
      compact
    )
  ) {
    return { reset: true };
  }

  let work = compact.replace(/^(zoom|cut(?:\s+plot)?)\s+/i, "").trim();

  const out = {};
  const pull = (letter, key) => {
    const re = axisRangeRegex(letter);
    const m = work.match(re);
    if (!m) {
      return;
    }
    const pair = normalizePair(m[1], m[2]);
    if (!pair) {
      return;
    }
    out[key] = pair;
    work = `${work.slice(0, m.index)} ${work.slice(m.index + m[0].length)}`
      .replace(/\s+/g, " ")
      .trim();
  };

  pull("x", "x");
  pull("y", "y");
  pull("z", "z");

  if (Object.keys(out).length === 0) {
    return null;
  }

  const leftover = work
    .replace(/\b(and|,|;)\b/gi, " ")
    .replace(/\s+/g, "")
    .trim();
  if (leftover.length > 0) {
    return null;
  }

  return out;
}

export function describePlotViewZoomReply(spec) {
  if (!spec) {
    return "";
  }
  if (spec.reset) {
    return "Plot view reset to the full data range.";
  }
  const parts = [];
  if (spec.x) {
    parts.push(`x from ${spec.x[0]} to ${spec.x[1]}`);
  }
  if (spec.y) {
    parts.push(`y from ${spec.y[0]} to ${spec.y[1]}`);
  }
  if (spec.z) {
    parts.push(`z from ${spec.z[0]} to ${spec.z[1]}`);
  }
  return `Updated the plot view (${parts.join(", ")}).`;
}
